import { useMemo } from 'react';
import useBasket from '~/features/commerce-api/hooks/useBasket';
import useProducts from '~/features/commerce-api/hooks/useProducts';
import { Product } from '~/lib/data-contract/salesforce';
import { ascertainNumber } from '~/shared/utils/numbers';
import { useAppliedSpecialPricing } from './useAppliedSpecialPrice';
import { isSpecialPriceApplied } from './utils';

const getLineBonusEarning = (basketProduct: Product): number => {
    const bonusPerUnit = ascertainNumber(basketProduct.c_potentialBonusEarning, {
        parser: parseFloat,
    });

    const quantity = ascertainNumber(basketProduct.quantity ?? 1);

    return bonusPerUnit * quantity;
};

/**
 * `c_totalBonusPrice` is meant to be the aggregate of bonus points from Salesforce.
 * But, due to an ongoing issue, we will re-evaluate it until it has been resolved.
 *
 * TODO: Remove implementation once c_totalBonusPrice calculation from Salesforce has been corrected.
 */
export const useTotalBonusEarning = () => {
    const { c_totalBonusPrice } = useBasket();
    const { products: basketProducts, isProductDataReady } = useProducts();
    const { promotions } = useAppliedSpecialPricing();

    const reportedBonusEarning = ascertainNumber(c_totalBonusPrice, { parser: parseFloat });

    /** Bonus wrongly included by Salesforce for lines with an applied Special Price. */
    const excludedBonusEarning = useMemo(
        () =>
            basketProducts.reduce<number>((total, basketProduct) => {
                // Cross-match the line's campaign against the aggregated, real-time promotions.
                const promotionId = basketProduct.c_specialPrices?.id;
                const promotion = promotionId ? promotions?.[promotionId] : undefined;

                if (!isSpecialPriceApplied(promotion)) return total;

                return total + getLineBonusEarning(basketProduct);
            }, 0),
        [basketProducts, promotions],
    );

    const totalBonusEarning = Math.max(reportedBonusEarning - excludedBonusEarning, 0);

    return {
        /** Bonus the customer actually earns. Use this for display. */
        totalBonusEarning,
        /** Raw `c_totalBonusPrice` from Salesforce, before correction. */
        reportedBonusEarning,
        /** Amount removed from the reported total because of applied Special Prices. */
        excludedBonusEarning,
        isReady: isProductDataReady,
        hasBonusEarning: isProductDataReady && totalBonusEarning > 0,
    };
};
